export const normalizeDecimal = (value: string): string => value.replace(',', '.');

type FormikValidationSync = {
  setValues: (values: any, shouldValidate?: boolean) => Promise<any> | void;
  validateForm: (values?: any) => Promise<Record<string, string>>;
  setTouched: (touched: Record<string, boolean>, shouldValidate?: boolean) => void;
};

export async function loadFormikValuesWithValidation(
  formik: FormikValidationSync,
  values: any,
): Promise<void> {
  await formik.setValues(values, true);
  const errors = await formik.validateForm(values);
  const invalidFields = Object.keys(errors);

  if (invalidFields.length) {
    formik.setTouched(
      invalidFields.reduce<Record<string, boolean>>((acc, fieldName) => {
        acc[fieldName] = true;
        return acc;
      }, {}),
      false,
    );
  }
}

export const validateDecimalField = (
  value: string,
  rangeRegex: RegExp,
  rangeError: string,
  precisionRegex: RegExp,
): string | undefined => {
  if (/[.,]$/.test(value)) return undefined;
  const normalized = normalizeDecimal(value);
  if (!rangeRegex.test(normalized)) return rangeError;
  if (!precisionRegex.test(normalized)) return 'Incorrect precision';
  return undefined;
};

export const integerValidator = /(^-?\d+$)/; // step 1
export const precisionValidator = /^-?\d*(([.,]\d{1})?)$/; // step 0.1
export const precisionValidator2 = /^-?\d*(([.,]\d{1,2})?)$/; // step 0.01
export const precisionValidator3 = /^-?\d*(([.,]\d{1,3})?)$/; // step 0.001
export const precisionValidator4 = /^-?\d*(([.,]\d{1,4})?)$/; // step 0.0001
export const precisionValidator6 = /^-?\d*(([.,]\d{1,6})?)$/; // step 0.000001

export const distanceValidation = /(^(2([.,]0+)?|1([.,]\d+)?|0?([.,]\d+)?)$)/; // [0,2]
export const degreeValidatior = /(^-?(50([.,]0+)?|[1-4]?\d([.,]\d+)?|0?([.,]\d+)?)$)/; // [-50,50]
export const rotationValidatior = /(^-?(360([.,]0+)?|3[0-5]\d([.,]\d+)?|[12]?\d{1,2}([.,]\d+)?|0?([.,]\d+)?)$)/; // [-360,360]
export const dthValidation = /^[1-9]\d*$/;

export const millionValidatior = /(^-?(1000000([.,]0+)?|[1-9]\d{0,5}([.,]\d+)?|0([.,]\d+)?)$)/; // [-1000000,1000000]

export const INTERIOR_CAMERA_VIEW_WIDTH_PX = 1600;

export const cameraBlockageValidator = /^((1600)|(1[0-5]\d{2})|([0-9]{1,3}))$/;

export const validateBlockageColumnWidthField = (value: string): string | undefined => {
  if (/[.,]$/.test(value)) return undefined;
  const normalized = normalizeDecimal(value);
  if (!integerValidator.test(normalized)) {
    return 'Can contain only whole numbers';
  }
  if (!cameraBlockageValidator.test(normalized)) {
    return `Can contain only numbers in range from 0 up to ${INTERIOR_CAMERA_VIEW_WIDTH_PX}`;
  }
  return undefined;
};

export const validateBlockageColumnWidthsSum = (
  leftValue: string | number | undefined,
  rightValue: string | number | undefined,
): string | undefined => {
  if (leftValue === undefined || rightValue === undefined) return undefined;
  const leftStr = leftValue.toString();
  const rightStr = rightValue.toString();
  if (!leftStr.length || !rightStr.length) return undefined;
  if (validateBlockageColumnWidthField(leftStr) || validateBlockageColumnWidthField(rightStr)) {
    return undefined;
  }
  const left = Number(normalizeDecimal(leftStr));
  const right = Number(normalizeDecimal(rightStr));
  if (left + right > INTERIOR_CAMERA_VIEW_WIDTH_PX) {
    return `Left and right blockage region widths cannot exceed ${INTERIOR_CAMERA_VIEW_WIDTH_PX} px combined`;
  }
  return undefined;
};
export const speedLimitValidator = /^((200)|(1[0-9]{2})|([1-9][0-9]{1})|([5-9]))$/;
export const hardBrakeLvl1ThresholdValidator = /(^-([0-9]|1[0-9])([.,]\d{1})?$)|(^(0)$)|(^(-20)$)/;
export const hardAccelerationLvl1ThresholdValidator = /(^([0-9]|1[0-9])([.,]\d{1})?$)|(^(0)$)|(^(20)$)/;
export const highGLvl1ThresholdValidator = /(^([0-9]|1[0-9])([.,]\d{1})?$)|(^(0)$)|(^(20)$)/;
export const highGLvl2ThresholdValidator = /(^([0-9]|1[0-9])([.,]\d{1})?$)|(^(0)$)|(^(20)$)/;
export const hardTurnLvl1ThresholdValidator = /(^([0-9]|1[0-9])([.,]\d{1})?$)|(^(0)$)|(^(20)$)/;

export const vehicleWidthValidation = /(^(5([.,]0+)?|[0-4]([.,]\d+)?|0?([.,]\d+)?)$)/; // [0,5]
export const vehicleHeightValidation = /(^(6([.,]0+)?|[0-5]?([.,]\d+)?|[1-5]([.,]\d+)?)$)/; // [0,6]
export const vehicleLengthValidation = /(^(30([.,]0+)?|[0-2]?\d([.,]\d+)?|0?([.,]\d+)?)$)/; // [0,30]
export const vehicleDistanceCameraToFrontOfVehicleValidation = /(^-?(4([.,]0+)?|[0-3]([.,]\d+)?|0?([.,]\d+)?)$)/; // [-4,4]
export const vehicleLateralCameraOffsetValidation = /(^-?(2([.,]0+)?|1([.,]\d+)?|0?([.,]\d+)?)$)/; // [-2,2]

export const haExtrinsicsCameraOffsetZValidation = /(^(4([.,]0+)?|[0-3]?([.,]\d+)?|[1-3]([.,]\d+)?)$)/; // [0,4]
export const cameraLocalLengthValidation = /(^(1000000([.,]0+)?|[1-9]\d{0,5}([.,]\d+)?|0([.,]\d+)?)$)/; // [0,1000000]
export const сameraPrincipalPointValidation = /(^(10000([.,]0+)?|[1-9]\d{0,3}([.,]\d+)?|0([.,]\d+)?)$)/; // [0,10000]
export const haGeneralProximityValidation = /(^(100([.,]0+)?|[1-9]?\d([.,]\d+)?|0([.,]\d+)?)$)/; // [0,100]
export const haGeneralCollisionThresholdValidation = /(^(0([.,]\d+)?|1([.,]0+)?)$)/; // [0,1]
export const haGeneralAlertSustainTimeSValidation = /(^(10([.,]0+)?|[0-9]([.,]\d+)?|0([.,]\d+)?)$)/; // [0,10]
